import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  SlackIntegrationError,
  SlackIntegrationErrorCode,
} from '@webapp/platform/integrations/business-logic/errors/SlackIntegrationError'
import { type ISlackIntegrationRepository } from '@webapp/platform/integrations/business-logic/interfaces/ISlackIntegrationRepository'
import { type SlackUserConstructor } from '@webapp/platform/integrations/entities/SlackUser/SlackUser'
import { type GetSlackUsersResponse } from '../../api-models/SlackUserApiModel'
import { mapSlackUserApiModel } from '../../mappers/mapSlackUserApiModel'

interface Dependency {
  httpClient: IHttpClient
}

interface ListSlackUsersParams {
  collection?: string
  orderBy?: string
  search?: string
  page?: number
  limit?: number | string
}

interface SlackDetailErrorResponse {
  detail: {
    message: string
  }
}

function isSlackDetailError(
  response: unknown
): response is SlackDetailErrorResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'detail' in response &&
    typeof (response as SlackDetailErrorResponse).detail === 'object' &&
    (response as SlackDetailErrorResponse).detail !== null &&
    typeof (response as SlackDetailErrorResponse).detail.message === 'string'
  )
}

const classifySlackError = (error: unknown): SlackIntegrationErrorCode => {
  if (
    error instanceof RepoError &&
    error.status === 401 &&
    error.message === 'token_revoked'
  ) {
    return SlackIntegrationErrorCode.TOKEN_REVOKED
  }
  return SlackIntegrationErrorCode.DEFAULT
}

export class SlackIntegrationRepository implements ISlackIntegrationRepository {
  constructor(private readonly deps: Dependency) {}

  async listSlackUsers(
    params?: ListSlackUsersParams
  ): Promise<RepoPaginatedResponse<SlackUserConstructor>> {
    try {
      const res = await this.deps.httpClient.get<GetSlackUsersResponse>(
        `/api/v3/integrations/slack/users/`,
        {
          params: {
            collection: params?.collection,
            order_by: params?.orderBy,
            search: params?.search,
            page: params?.page,
            limit: params?.limit,
          },
        }
      )

      const users = res.data.data
      const metadata = res.data.metadata

      return {
        data: users.map((user) => mapSlackUserApiModel(user)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw new SlackIntegrationError(classifySlackError(err))
    }
  }

  async linkUser(procurifyUserId: number, slackUserId: string): Promise<void> {
    try {
      await this.deps.httpClient.put(
        `/api/v2/slack/links/${procurifyUserId}/`,
        { slack_user_id: slackUserId }
      )
    } catch (err) {
      throw new SlackIntegrationError(classifySlackError(err))
    }
  }

  async unlinkUser(procurifyUserId: number): Promise<void> {
    try {
      await this.deps.httpClient.put(
        `/api/v2/slack/links/${procurifyUserId}/`,
        { slack_user_id: null }
      )
    } catch (err) {
      throw new SlackIntegrationError(classifySlackError(err))
    }
  }

  async syncUnlinkedUsers(): Promise<void> {
    try {
      await this.deps.httpClient.post(`/api/v2/slack/links/`, {})
    } catch (err) {
      throw new SlackIntegrationError(classifySlackError(err))
    }
  }
  async deleteSlackIntegration(): Promise<void> {
    try {
      await this.deps.httpClient.delete(
        '/api/v3/integrations/slack/disconnect/'
      )
    } catch (err) {
      throw new SlackIntegrationError(classifySlackError(err))
    }
  }
}
